/**
 * `storePacks.*` — Store Admin size scales + versioned pack library (Commerce, spec 13).
 *
 * Garments wholesale in packs, never loose pieces. A store defines size scales
 * once and builds reusable, versioned packs on them; every product is then
 * priced per pack. Writes need `store.catalog.manage`, reads `store.catalog.view`
 * (R9). Composition/pieces are the pack's, derived server-side — the wire only
 * carries `{label → qty}`.
 *
 * @see packages/domain/src/commerce/store · docs/spec/13-store-pack-pricing.md
 */
import { z } from "zod";
import { uuid } from "../_common.js";

// ── shared field schemas (R1/R2) ───────────────────────────────────
export const scaleName = z.string().trim().min(1).max(32);
export const sizeLabel = z.string().trim().min(1).max(8);
export const packName = z.string().trim().min(1).max(32);

/** `{label → qty}` — non-empty, each qty a whole number 1–1000. */
export const composition = z
  .record(sizeLabel, z.number().int().min(1).max(1000))
  .refine((c) => Object.keys(c).length > 0, { message: "Set at least one size." });

export const packStatus = z.enum(["active", "archived"]);
export type PackStatus = z.infer<typeof packStatus>;

// ── size scales ────────────────────────────────────────────────────
export const sizeScaleRow = z.object({
  scaleId: uuid,
  name: z.string(),
  labels: z.array(z.string()),
  /** A pack depends on this scale → only label-append is offered, never edit. */
  referenced: z.boolean(),
  createdAt: z.string().datetime(),
});
export type SizeScaleRow = z.infer<typeof sizeScaleRow>;

export const listScalesOutput = z.array(sizeScaleRow);

export const createScaleInput = z.object({
  name: scaleName,
  labels: z.array(sizeLabel).min(1).max(24),
});
export const createScaleOutput = z.object({ scaleId: uuid });

/**
 * Find-or-create the scale for an ordered label list (issue #235). The Bundle
 * Builder's sizes come from the product's Size variant axis; this bridges them
 * to the scale a pack composition must key on. Exact ordered match wins;
 * otherwise a scale is minted with a generated name ("S/M/L/XL").
 */
export const ensureScaleInput = z.object({
  labels: z.array(sizeLabel).min(1).max(24),
});
export const ensureScaleOutput = z.object({ scaleId: uuid, created: z.boolean() });

export const appendLabelInput = z.object({ scaleId: uuid, label: sizeLabel });
export const appendLabelOutput = z.object({ scaleId: uuid, labels: z.array(z.string()) });

// ── packs ──────────────────────────────────────────────────────────
export const packRow = z.object({
  packId: uuid,
  familyId: uuid,
  version: z.number().int(),
  name: z.string(),
  scaleId: uuid,
  scaleName: z.string(),
  composition: z.record(z.string(), z.number().int()),
  pieces: z.number().int(),
  status: packStatus,
  /** Listings pricing against this exact version — the "Used by" count. */
  productsUsing: z.number().int().nonnegative(),
  /** Cited by any pricing entry / order / quote line → Edit is replaced by New version. */
  referenced: z.boolean(),
  createdAt: z.string().datetime(),
});
export type PackRow = z.infer<typeof packRow>;

export const listPacksOutput = z.array(packRow);

export const createPackInput = z.object({
  name: packName,
  scaleId: uuid,
  composition,
});
export const createPackOutput = z.object({
  packId: uuid,
  familyId: uuid,
  pieces: z.number().int(),
});

/** New version of a referenced pack (or explicit bump of an unreferenced one). */
export const versionPackInput = z.object({
  packId: uuid,
  name: packName.optional(),
  composition,
});
export const versionPackOutput = z.object({
  packId: uuid,
  familyId: uuid,
  version: z.number().int(),
  pieces: z.number().int(),
});

/** In-place edit — server rejects it if the pack is referenced (R3). */
export const editPackInput = z.object({
  packId: uuid,
  name: packName.optional(),
  /**
   * Re-base an unreferenced pack onto a different scale; the composition is then
   * validated against the NEW scale's labels. Absent = keep the current scale.
   * Not offered for `version` — a family stays on the scale it was created on.
   */
  scaleId: uuid.optional(),
  composition,
});
export const editPackOutput = z.object({ packId: uuid, pieces: z.number().int() });

export const archivePackInput = z.object({ packId: uuid });
export const archivePackOutput = z.object({ packId: uuid, status: packStatus });

// ── derived TS types ──────────────────────────────────────────────
export type ListScalesOutput = z.infer<typeof listScalesOutput>;
export type CreateScaleInput = z.infer<typeof createScaleInput>;
export type AppendLabelInput = z.infer<typeof appendLabelInput>;
export type ListPacksOutput = z.infer<typeof listPacksOutput>;
export type CreatePackInput = z.infer<typeof createPackInput>;
export type VersionPackInput = z.infer<typeof versionPackInput>;
export type EditPackInput = z.infer<typeof editPackInput>;
export type ArchivePackInput = z.infer<typeof archivePackInput>;
